import { CheckCircleIcon, EmailIcon } from '@chakra-ui/icons'
import {
  Box,
  Divider,
  Flex,
  IconButton,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  ConsentFormStatusVersion,
  getDateTimeString,
  makePluralIfNecessary,
  PatientConsentFormStatus,
} from '@hb/shared'
import historyIcon from '../../../icons/history.svg'
import { ActionButton, SolidActionButton } from '../../Buttons/ActionButton'
import { DeleteConsentFormButton } from './DeleteConsentForm'

const ConsentFormStatusVersionView = ({ version }: { version: ConsentFormStatusVersion }) => (
  <Flex flexDir="column">
    <Text fontSize="lg" fontWeight={500}>
      {version.name}
    </Text>
    <Flex align="center" gap={1.5}>
      <EmailIcon w={4} h={4} color="blackAlpha.500" _dark={{ color: 'gray.400' }} />
      <Text fontWeight={500} color="gray.500">
        SENT {getDateTimeString(version.sentOn, 'short')}
      </Text>
    </Flex>
    <Flex align="center" gap={1.5}>
      <CheckCircleIcon
        w={4}
        h={4}
        color={version.signedOn ? 'green.500' : 'blackAlpha.500'}
        _dark={{ color: version.signedOn ? 'green.400' : 'gray.400' }}
      />
      <Text
        fontWeight={500}
        color={version.signedOn ? 'green.500' : 'gray.500'}
        _dark={{ color: version.signedOn ? 'green.400' : 'gray.400' }}>
        {version.signedOn ? `SIGNED ${getDateTimeString(version.signedOn, 'short')}` : 'NOT SIGNED'}
      </Text>
    </Flex>
  </Flex>
)

const PreviousVersionView = ({
  version,
  handleViewConsentForm,
  adminView,
  patientId,
}: {
  version: ConsentFormStatusVersion
  handleViewConsentForm?: (formId: string) => void
  adminView?: boolean
  patientId: string | null
}) => (
  <Flex alignItems="center" w="100%" gap={2} px={3} py={1} key={version.consentFormId}>
    <Box flex={1} minW="0">
      <ConsentFormStatusVersionView version={version} />
    </Box>
    {adminView ? (
      <DeleteConsentFormButton patientId={patientId} consentFormStatus={version} />
    ) : null}
    {handleViewConsentForm ? (
      <ActionButton size="sm" onClick={() => handleViewConsentForm(version.consentFormId)}>
        View
      </ActionButton>
    ) : null}
  </Flex>
)

const PreviousVersionsPopover = ({
  previousVersions,
  patientId,
  handleViewConsentForm,
  adminView,
}: {
  previousVersions: ConsentFormStatusVersion[]
  patientId: string | null
  handleViewConsentForm?: (formId: string) => void
  adminView?: boolean
}) => {
  if (previousVersions.length === 0) {
    return null
  }
  return (
    <Popover strategy="fixed" trigger="hover" placement="left-start">
      <PopoverTrigger>
        <IconButton
          borderRadius="full"
          size="sm"
          variant="ghost"
          aria-label="View previous versions"
          icon={<Image _dark={{ filter: 'invert(1)' }} src={historyIcon} />}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="400px">
          <PopoverHeader
            borderTopRadius={4}
            bg="gray.50"
            _dark={{ bg: 'gray.900' }}
            py={1}
            fontWeight={500}
            fontFamily="hero-new">
            {previousVersions.length} Previous{' '}
            {makePluralIfNecessary('Version', previousVersions.length)}
          </PopoverHeader>
          <PopoverBody p={0}>
            <VStack w="100%" divider={<Divider />} spacing={0}>
              {previousVersions.map(version => (
                <PreviousVersionView
                  version={version}
                  key={version.consentFormId}
                  handleViewConsentForm={handleViewConsentForm}
                  adminView={adminView}
                  patientId={patientId}
                />
              ))}
            </VStack>
          </PopoverBody>
          <PopoverArrow bg="gray.50" />
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export const ConsentFormStatusView = ({
  formStatus,
  handleViewConsentForm,
  patientId,
  adminView,
}: {
  formStatus: PatientConsentFormStatus
  patientId: string | null
  handleViewConsentForm?: (formId: string) => void
  adminView?: boolean
}) => {
  const ButtonComponent = formStatus.signedOn ? ActionButton : SolidActionButton

  return (
    <Flex w="100%" gap={2} flexFlow={['column', 'column', 'row']} justifyContent="space-between">
      <Box py={2} px={4} flex={1} minW="0">
        <ConsentFormStatusVersionView version={formStatus} />
      </Box>
      <Flex
        justifyContent={adminView ? 'flex-start' : 'center'}
        alignItems="flex-end"
        pb={1}
        pt={3}
        pr={3}
        flexFlow="column">
        <Flex flexFlow="row" align="center" gap={2}>
          <PreviousVersionsPopover
            previousVersions={formStatus.previousVersions}
            handleViewConsentForm={handleViewConsentForm}
            patientId={patientId}
            adminView={adminView}
          />
          {handleViewConsentForm ? (
            <ButtonComponent
              size="sm"
              mb={[2, 2, 0]}
              w={['100%', '100%', 'auto']}
              onClick={() => handleViewConsentForm(formStatus.consentFormId)}
              variant={formStatus.signedOn ? 'outline' : 'solid'}>
              {formStatus.signedOn ? 'View Signed Form' : 'View Form'}
            </ButtonComponent>
          ) : null}
        </Flex>
        {adminView ? (
          <Box mt={3} ml="auto">
            <DeleteConsentFormButton patientId={patientId} consentFormStatus={formStatus} />
          </Box>
        ) : null}
      </Flex>
    </Flex>
  )
}
